import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  faDownload,
  faEye,
  faFileExcel,
  faFilePdf,
  faPenToSquare,
  faPlus,
  faUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import {
  App,
  Button,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Select,
  Skeleton,
  Space,
  Spin,
  Table,
  Tag,
} from 'antd';
import { styled } from 'styled-components';
import { useShippingManifest, useShippingManifests } from '@/api/Vessel';
import { vessel } from '@/api/Vessel/Vessel';
import { useTranslate } from '@/translations';
import { CustomColumns, CustomTable, Icon, TooltipIcon } from '@/ui/components';
import {
  ManifestSearch,
  ShippingManifestDetail,
  ShippingManifestSummary,
} from '@/types';
import { AddManifestModal, EditManifestModal } from './ManifestModal';
import { downloadManifestPdf, downloadVehicleList } from './manifestDownloads';
import { formatReferenceVinRange } from './manifestEditor';
import { filterManifestRows, manifestListStatus } from './manifestList';
import { timeConverter } from '@/utils/helpers';

type ManifestsTabProps = {
  vesselName?: string;
  canOperate: boolean;
  onViewPortCalls?: () => void;
  onViewVessel?: () => void;
};

type SearchDraft = ManifestSearch & {
  status?: '' | 'active' | 'planned';
  dateFrom?: string;
  dateTo?: string;
};

type FilterFormValues = {
  manifestRef?: string;
  vessel?: string;
  customer?: string;
  shippingReference?: string;
  vin?: string;
  site?: string;
  status?: '' | 'active' | 'planned';
  dateFrom?: dayjs.Dayjs | null;
  dateTo?: dayjs.Dayjs | null;
};

const emptyFilterValues = (vesselName?: string): FilterFormValues => ({
  manifestRef: '',
  vessel: vesselName || '',
  customer: '',
  shippingReference: '',
  vin: '',
  site: '',
  status: '',
  dateFrom: null,
  dateTo: null,
});

const toSearch = (values: FilterFormValues): SearchDraft => ({
  manifestRef: values.manifestRef,
  vessel: values.vessel,
  customer: values.customer,
  shippingReference: values.shippingReference,
  vin: values.vin,
  site: values.site,
  status: values.status || '',
  dateFrom: values.dateFrom?.startOf('day').toISOString(),
  dateTo: values.dateTo?.endOf('day').toISOString(),
});

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
`;

const FilterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
`;

const Heading = styled.h3`
  margin: 0 0 12px;
  font-size: 16px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 0.9fr) minmax(240px, 1.2fr) minmax(
      200px,
      0.9fr
    );
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const DetailCard = styled.section`
  padding: 12px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
`;

const DetailTitle = styled.span`
  display: block;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
`;

const saveBlob = (data: Blob | undefined, fileName: string) => {
  if (!data) return;
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const ManifestsTab = ({
  vesselName,
  canOperate,
  onViewPortCalls,
  onViewVessel,
}: ManifestsTabProps) => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const [filterForm] = Form.useForm<FilterFormValues>();
  const [applied, setApplied] = useState<SearchDraft>(() => ({
    vessel: vesselName || '',
    status: '',
  }));
  const [expandedId, setExpandedId] = useState<number>();
  const [editId, setEditId] = useState<number>();
  const [downloadingId, setDownloadingId] = useState<number>();
  const [addOpen, setAddOpen] = useState(false);

  const manifests = useShippingManifests({
    manifestRef: applied.manifestRef,
    vessel: applied.vessel,
    customer: applied.customer,
    shippingReference: applied.shippingReference,
    vin: applied.vin,
    site: applied.site,
  });
  const { data: detail, isFetching: isDetailFetching } =
    useShippingManifest(expandedId);
  const editDetail = useShippingManifest(editId);

  useEffect(() => {
    filterForm.setFieldsValue(emptyFilterValues(vesselName));
    setApplied({ vessel: vesselName || '', status: '' });
  }, [filterForm, vesselName]);

  const rows = useMemo(
    () =>
      filterManifestRows(manifests.data ?? [], {
        status: applied.status,
        dateFrom: applied.dateFrom,
        dateTo: applied.dateTo,
      }),
    [applied.dateFrom, applied.dateTo, applied.status, manifests.data],
  );

  const downloadByType = async (
    id: number,
    type: 'manifest' | 'vehicle-list',
    fileName: string,
  ) => {
    setDownloadingId(id);
    try {
      const response = await vessel.downloadShippingManifestByType(id, type);
      if (!response.success || !response.data) {
        message.info(t('manifestDocumentMissing'));
        return;
      }
      saveBlob(response.data, fileName);
    } finally {
      setDownloadingId(undefined);
    }
  };

  const columns: CustomColumns<ShippingManifestSummary> = [
    {
      title: t('manifestRef'),
      dataIndex: 'manifestRef',
      width: 140,
      fixed: 'left',
    },
    {
      title: t('vessel'),
      dataIndex: 'vesselName',
      width: 180,
      fixed: 'left',
    },
    { title: t('voyage'), dataIndex: 'voyage', width: 100 },
    { title: t('siteTerminal'), dataIndex: 'site', width: 160 },
    {
      title: t('eta'),
      dataIndex: 'eta',
      width: 180,
      render: (value?: string) => timeConverter(value),
    },
    {
      title: t('shippingRefs'),
      dataIndex: 'shippingReferenceCount',
      width: 110,
    },
    { title: t('vehicles'), dataIndex: 'vehicleCount', width: 100 },
    { title: t('customer'), dataIndex: 'customerName', width: 160 },
    {
      dataIndex: 'eta',
      title: t('status'),
      width: 110,
      fixed: 'right',
      render: (_, record) => {
        const planned = manifestListStatus(record.eta) === 'planned';
        return (
          <Tag color={planned ? 'blue' : 'green'}>
            {planned ? t('planned') : t('manifestActive')}
          </Tag>
        );
      },
    },
    {
      dataIndex: 'id',
      title: t('actions'),
      width: 130,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <TooltipIcon
            title={t('manifestOverview')}
            icon={faEye}
            onClick={() =>
              setExpandedId((current) =>
                current === record.id ? undefined : record.id,
              )
            }
          />
          {canOperate ? (
            <TooltipIcon
              title={t('editManifest')}
              icon={faPenToSquare}
              onClick={() => setEditId(record.id)}
            />
          ) : null}
          {downloadingId === record.id ? (
            <Spin size="small" />
          ) : (
            <TooltipIcon
              title={t('download')}
              icon={faDownload}
              onClick={() =>
                void downloadByType(
                  record.id,
                  'manifest',
                  `Manifest ${record.manifestRef}.pdf`,
                )
              }
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Toolbar>
        <Filters>
          <Form
            form={filterForm}
            component="div"
            layout="vertical"
            style={{ display: 'contents' }}
            initialValues={emptyFilterValues(vesselName)}
            onFinish={(values) => setApplied(toSearch(values))}
          >
            <Form.Item
              name="manifestRef"
              label={t('manifestRef')}
              style={{ marginBottom: 0 }}
            >
              <Input allowClear placeholder={t('manifestRef')} />
            </Form.Item>
            <Form.Item
              name="vessel"
              label={t('vessel')}
              style={{ marginBottom: 0 }}
            >
              <Input allowClear placeholder={t('chooseVessel')} />
            </Form.Item>
            <Form.Item
              name="customer"
              label={t('customer')}
              style={{ marginBottom: 0 }}
            >
              <Input allowClear placeholder={t('chooseCustomer')} />
            </Form.Item>
            <Form.Item
              name="shippingReference"
              label={t('shippingReference')}
              style={{ marginBottom: 0 }}
            >
              <Input allowClear placeholder={t('enterReference')} />
            </Form.Item>
            <Form.Item name="vin" label="VIN" style={{ marginBottom: 0 }}>
              <Input allowClear placeholder={t('enterVin')} />
            </Form.Item>
            <Form.Item
              name="site"
              label={t('siteTerminal')}
              style={{ marginBottom: 0 }}
            >
              <Input allowClear placeholder={t('allSites')} />
            </Form.Item>
            <Form.Item
              name="status"
              label={t('status')}
              style={{ marginBottom: 0 }}
            >
              <Select
                style={{ width: 140 }}
                options={[
                  { value: '', label: t('allStatuses') },
                  { value: 'active', label: t('manifestActive') },
                  { value: 'planned', label: t('planned') },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="dateFrom"
              label={t('dateFrom')}
              style={{ marginBottom: 0 }}
            >
              <DatePicker placeholder={t('chooseDate')} />
            </Form.Item>
            <Form.Item
              name="dateTo"
              label={t('dateTo')}
              style={{ marginBottom: 0 }}
            >
              <DatePicker placeholder={t('chooseDate')} />
            </Form.Item>
            <FilterActions>
              <Button type="primary" htmlType="submit">
                {t('search')}
              </Button>
              <Button
                onClick={() => {
                  filterForm.setFieldsValue(emptyFilterValues());
                  setApplied({ vessel: '', status: '' });
                }}
              >
                {t('clearSearch')}
              </Button>
              <Button
                type="primary"
                icon={<Icon icon={faPlus} />}
                onClick={() => setAddOpen(true)}
              >
                {t('addManifest')}
              </Button>
            </FilterActions>
          </Form>
        </Filters>
      </Toolbar>
      <Heading>{t('allShippingManifests', { count: rows.length })}</Heading>
      <CustomTable
        rowKey="id"
        loading={manifests.isFetching}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 5 }}
        scroll={{ x: 1600 }}
        expandable={{
          expandedRowKeys: expandedId ? [expandedId] : [],
          onExpand: (expanded, record) =>
            setExpandedId(expanded ? record.id : undefined),
          expandedRowRender: () =>
            isDetailFetching ? (
              <Skeleton active />
            ) : (
              <ManifestOverview
                detail={detail}
                onViewPortCalls={onViewPortCalls}
                onViewVessel={onViewVessel}
                onDownload={(type) => {
                  if (!detail) return Promise.resolve();
                  if (type === 'manifest') downloadManifestPdf(detail);
                  else downloadVehicleList(detail);
                  return Promise.resolve();
                }}
              />
            ),
        }}
      />
      <AddManifestModal open={addOpen} onClose={() => setAddOpen(false)} />
      <EditManifestModal
        open={Boolean(editId)}
        loading={editDetail.isPending || editDetail.isFetching}
        detail={editDetail.data}
        onClose={() => setEditId(undefined)}
        onDownload={async (documentId, fileName) => {
          if (!editId) return;
          const response = await vessel.downloadShippingManifestDocument(
            editId,
            documentId,
          );
          saveBlob(response.data, fileName);
        }}
      />
    </>
  );
};

const ManifestOverview = ({
  detail,
  onViewPortCalls,
  onViewVessel,
  onDownload,
}: {
  detail?: ShippingManifestDetail;
  onViewPortCalls?: () => void;
  onViewVessel?: () => void;
  onDownload: (documentType: 'manifest' | 'vehicle-list') => Promise<void>;
}) => {
  const { t } = useTranslate();
  if (!detail) return null;
  const vehicles = detail.shippingReferences.reduce(
    (sum, reference) => sum + reference.vehicleCount,
    0,
  );

  return (
    <DetailGrid>
      <DetailCard>
        <DetailTitle>{t('manifestOverview')}</DetailTitle>
        <Descriptions
          column={1}
          size="small"
          bordered
          className="light-content"
          items={[
            {
              key: 'manifestRef',
              label: t('manifestRef'),
              children: detail.manifestRef,
            },
            {
              key: 'vessel',
              label: t('vessel'),
              children: (
                <div className="text-link" onClick={onViewVessel}>
                  {detail.vesselName}
                </div>
              ),
            },
            {
              key: 'voyage',
              label: t('voyage'),
              children: detail.voyage || '—',
            },
            {
              key: 'site',
              label: t('siteTerminal'),
              children: detail.site || detail.dischargePort || '—',
            },
            {
              key: 'destination',
              label: t('destinationPort'),
              children: detail.dischargePort || '—',
            },
            {
              key: 'documents',
              label: t('documents'),
              children: (
                <>
                  <div
                    className="d-flex align-items-center mb-1"
                    style={{ gap: 8, cursor: 'pointer' }}
                    onClick={() => onDownload('manifest')}
                  >
                    <Icon icon={faFilePdf} color="#e53935" />
                    <span className="text-link">
                      Manifest {detail.manifestRef}.pdf
                    </span>
                  </div>
                  <div
                    className="d-flex align-items-center mb-1"
                    style={{ gap: 8, cursor: 'pointer' }}
                    onClick={() => onDownload('vehicle-list')}
                  >
                    <Icon icon={faFileExcel} color="#217346" />
                    <span className="text-link">
                      Vehicle list {detail.manifestRef}.xlsx
                    </span>
                  </div>
                </>
              ),
            },
          ]}
        />
      </DetailCard>
      <DetailCard>
        <DetailTitle>{t('shippingReferenceBreakdown')}</DetailTitle>
        <Table
          size="small"
          pagination={false}
          rowKey="id"
          dataSource={detail.shippingReferences}
          columns={[
            { title: t('shippingReference'), dataIndex: 'reference' },
            {
              title: t('vehicles'),
              dataIndex: 'vehicleCount',
              width: 90,
            },
            {
              title: t('vinRange'),
              render: (_, reference) =>
                formatReferenceVinRange(
                  reference.startVin,
                  reference.endVin,
                  reference.vehicleCount,
                ),
            },
          ]}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <strong>{t('totalVehicles')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong>{vehicles.toLocaleString()}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
            </Table.Summary.Row>
          )}
        />
      </DetailCard>
      <DetailCard>
        <DetailTitle>{t('relatedPortCall')}</DetailTitle>
        <Descriptions
          column={1}
          size="small"
          bordered
          className="light-content"
          items={[
            {
              key: 'portCall',
              label: t('destinationPort'),
              children: (
                <div className="text-link" onClick={onViewPortCalls}>
                  {detail.dischargePort || '—'}
                </div>
              ),
            },
            {
              key: 'vessel',
              label: t('vessel'),
              children: detail.vesselName || '—',
            },
            {
              key: 'site',
              label: t('siteTerminal'),
              children: detail.site || detail.dischargePort || '—',
            },
            {
              key: 'eta',
              label: t('eta'),
              children: timeConverter(detail.eta),
            },
          ]}
        />
        {onViewPortCalls ? (
          <div className="mt-2 d-flex align-items-center justify-content-center">
            <Button
              icon={<Icon icon={faUpRightFromSquare} />}
              onClick={onViewPortCalls}
              color="blue"
              variant="outlined"
            >
              {t('viewPortCallDetails')}
            </Button>
          </div>
        ) : null}
      </DetailCard>
    </DetailGrid>
  );
};
